import { z } from 'zod';

// Operator read over the e-mail log (IMM-1076.2). Kept in sync by hand with the
// backend `EmailKind` / `EmailLogStatus` domain types (the contract package cannot
// import backend code); the query handler's return type makes a backend token the
// contract lacks a compile error.
export const EmailLogKindSchema = z.enum([
  'MEMBER_INVITE',
  'NEW_LEAD',
  'LEAD_QUALIFIED',
  'VALUATION_RELEASED',
  'VIEWING_DECLINED',
  'ONBOARDING_INVITE',
  'ONBOARDING_SUBMITTED',
  'ONBOARDING_CHANGES_REQUESTED',
  'ONBOARDING_APPROVED',
  'OPERATOR_INVITE',
  'BROKERAGE_CONCLUDED',
  'BROKERAGE_WITHDRAWN',
  'VIEWING_INVITATION',
  'NEW_INQUIRY',
  'SIGNING_SIGNED',
  'SIGNING_COMPLETED',
  'SIGNING_REVOKED',
]);
export type EmailLogKind = z.infer<typeof EmailLogKindSchema>;

export const EmailLogStatusSchema = z.enum(['SENT', 'DELIVERED', 'BOUNCED', 'COMPLAINED', 'FAILED']);
export type EmailLogStatus = z.infer<typeof EmailLogStatusSchema>;

// `recipient` and `subject` are plain strings on purpose: a read field may not be
// stricter than what the senders stored (BL-039).
export const EmailLogListItemSchema = z.object({
  id: z.uuid(),
  kind: EmailLogKindSchema,
  recipient: z.string(),
  workspaceId: z.uuid().nullable(),
  // Null when the e-mail belongs to no workspace, or the workspace no longer resolves.
  workspaceName: z.string().nullable(),
  subject: z.string(),
  status: EmailLogStatusSchema,
  // When immoOS learned a delivery status; null while SENT or FAILED.
  statusAt: z.iso.datetime().nullable(),
  // The provider's error name for a FAILED entry; null otherwise.
  failureReason: z.string().nullable(),
  sentAt: z.iso.datetime(),
});
export type EmailLogListItem = z.infer<typeof EmailLogListItemSchema>;

export const ListEmailLogQuerySchema = z.object({
  q: z.string().trim().min(1).max(200).optional(),
  // Exact, case-insensitive match on the recipient address — the member page's
  // read, where a substring like `q` would match `joanna@…` for `anna@…`.
  recipient: z.string().trim().min(1).max(320).optional(),
  workspaceId: z.uuid().optional(),
  kind: EmailLogKindSchema.optional(),
  status: EmailLogStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  cursor: z.string().optional(),
});
export type ListEmailLogQuery = z.infer<typeof ListEmailLogQuerySchema>;

export const ListEmailLogResponseSchema = z.object({
  data: z.array(EmailLogListItemSchema),
  // Pass back as `?cursor=` for the next page; null when the list is exhausted.
  nextCursor: z.string().nullable(),
});
export type ListEmailLogResponse = z.infer<typeof ListEmailLogResponseSchema>;
